import { ceilPositiveMinutesToStep } from "../../../../../../../../../_shared/_logic/time-granularity"
import type { AppointmentLetterTodoAssignmentMode } from "../../../../../../../../../api/kehrblatt/planning"

export const DEFAULT_PLANNING_SETTINGS = {
  flex_slot_minutes: 60,
  flex_slots_per_day: 0,
  customer_appointment_window_delta_minutes: 30,
  customer_appointment_window_rounding_minutes: 0,
  appointment_letter_lead_days: 7,
  appointment_letter_auto_dispatch_enabled: false,
  appointment_letter_todo_assignment_mode: "COLLECTIVE" as AppointmentLetterTodoAssignmentMode,
  default_contact_sequence_template_id: "digital_standard",
} as const

const createNumberOptions = (values: readonly number[], label: (value: number) => string) =>
  values.map((value) => ({
    name: label(value),
    value: String(value),
  }))

export const flexSlotMinuteOptions = createNumberOptions(
  [5, 10, 15, 20, 25, 30, 45, 60, 75, 90, 120],
  (value) => {
    return `${value} Minuten`
  },
)

export const flexSlotsPerDayOptions = createNumberOptions([0, 1, 2, 3, 4, 5, 6], (value) => {
  if (value === 0) return "Keine Flextermine"

  return `${value} pro Tag`
})

export const customerAppointmentWindowDeltaOptions = createNumberOptions(
  [0, 15, 30, 45, 60, 90, 120],
  (value) => {
    if (value === 0) return "Kein Puffer"

    if (value === 60) return "1 Stunde"

    if (value === 90) return "90 Minuten"

    if (value === 120) return "2 Stunden"

    return `${value} Minuten`
  },
)

export const customerAppointmentWindowRoundingOptions = createNumberOptions(
  [0, 15, 30, 60],
  (value) => {
    if (value === 0) return "Keine Rundung"

    if (value === 15) return "Auf Viertelstunden erweitern"

    if (value === 30) return "Auf halbe Stunden erweitern"

    return "Auf volle Stunden erweitern"
  },
)

export const appointmentLetterLeadDayOptions = createNumberOptions(
  [3, 5, 7, 10, 12, 14],
  (value) => `${value} Tage vor der Terminwoche`,
)

export const appointmentLetterLeadExample = (value: string | number, automatic = false): string => {
  const days = Number(value)
  const week = days > 7 ? 36 : 37
  const weekday =
    days === 3 || days === 10 ? "Freitag" : days === 5 || days === 12 ? "Mittwoch" : "Montag"
  const sameDay = days === 3 || days === 10 ? " Die Aufgabe erscheint am Zustelltag." : ""

  if (automatic)
    return `Termine in KW 38: Versandbeauftragung ab ${weekday} in KW ${week}. Die tatsächliche Verarbeitung erfolgt anschließend durch den Versanddienstleister.`

  return `Termine in KW 38: Aufgabe ab ${weekday} in KW ${week}, Zustellung bis Freitag in KW ${week}.${sameDay}`
}

export const appointmentLetterTodoAssignmentModeOptions = [
  {
    name: "Gesammelt",
    value: "COLLECTIVE",
  },
  {
    name: "Je Mitarbeiter",
    value: "PER_EMPLOYEE",
  },
]

export const withCurrentNumberOption = (
  options: Array<{ name: string; value: string }>,
  current: number,
) => {
  const currentValue = String(current)

  if (options.some((option) => option.value === currentValue)) return options

  return [
    {
      name: `Aktueller Wert: ${current} (bitte neu auswählen)`,
      value: currentValue,
    },
    ...options,
  ]
}

export const parsePlanningSelectNumber = (value: string | number, fallback: number) => {
  const parsed = Number(value)

  return Number.isFinite(parsed) ? Math.floor(parsed) : fallback
}

export const parsePlanningStepMinutes = (value: string | number, fallback: number) =>
  ceilPositiveMinutesToStep(value, fallback) ?? fallback

export const parseAppointmentLetterTodoAssignmentMode = (
  value: string,
  fallback: AppointmentLetterTodoAssignmentMode,
): AppointmentLetterTodoAssignmentMode =>
  value === "PER_EMPLOYEE" || value === "COLLECTIVE" ? value : fallback

export const buildPlanningSettingsPayload = (
  values: {
    flexSlotMinutes: string | number
    flexSlotsPerDay: string | number
    customerAppointmentWindowDeltaMinutes: string | number
    customerAppointmentWindowRoundingMinutes: string | number
    appointmentLetterLeadDays: string | number
    appointmentLetterAutoDispatchEnabled?: boolean
    appointmentLetterTodoAssignmentMode: string
  },
  fallback: {
    flex_slot_minutes?: number | null
    flex_slots_per_day?: number | null
    customer_appointment_window_delta_minutes?: number | null
    customer_appointment_window_rounding_minutes?: number | null
    appointment_letter_lead_days?: number | null
    appointment_letter_todo_assignment_mode?: AppointmentLetterTodoAssignmentMode | null
  },
) => ({
  ...(values.appointmentLetterAutoDispatchEnabled === undefined
    ? {}
    : {
        appointment_letter_auto_dispatch_enabled: values.appointmentLetterAutoDispatchEnabled,
      }),
  flex_slot_minutes: parsePlanningStepMinutes(
    values.flexSlotMinutes,
    fallback.flex_slot_minutes ?? DEFAULT_PLANNING_SETTINGS.flex_slot_minutes,
  ),
  flex_slots_per_day: parsePlanningSelectNumber(
    values.flexSlotsPerDay,
    fallback.flex_slots_per_day ?? DEFAULT_PLANNING_SETTINGS.flex_slots_per_day,
  ),
  customer_appointment_window_delta_minutes: parsePlanningSelectNumber(
    values.customerAppointmentWindowDeltaMinutes,
    fallback.customer_appointment_window_delta_minutes ??
      DEFAULT_PLANNING_SETTINGS.customer_appointment_window_delta_minutes,
  ),
  customer_appointment_window_rounding_minutes: parsePlanningSelectNumber(
    values.customerAppointmentWindowRoundingMinutes,
    fallback.customer_appointment_window_rounding_minutes ??
      DEFAULT_PLANNING_SETTINGS.customer_appointment_window_rounding_minutes,
  ),
  appointment_letter_lead_days: parsePlanningSelectNumber(
    values.appointmentLetterLeadDays,
    fallback.appointment_letter_lead_days ?? DEFAULT_PLANNING_SETTINGS.appointment_letter_lead_days,
  ),
  appointment_letter_todo_assignment_mode: parseAppointmentLetterTodoAssignmentMode(
    values.appointmentLetterTodoAssignmentMode,
    fallback.appointment_letter_todo_assignment_mode ??
      DEFAULT_PLANNING_SETTINGS.appointment_letter_todo_assignment_mode,
  ),
})
